import { Injectable } from "@angular/core";
import { Observable, combineLatest, map, tap } from "rxjs";
import { Dictionary } from "@helpers/types";
import { Tradable } from "../entities/tradable";
import { TradablesBridgeService } from "../infrastructure/tradables.bridge.service";
import { TradablesStateService } from "./tradables.state.service";
import { TradablesDataService } from "@modules/+new/core/tradables/domain/infrastructure/tradables.data.service";
import { TradableInfo } from "@modules/+new/core/tradables/domain/entities/tradable-info";
import { TradablesInfoStateService } from "@modules/+new/core/tradables/domain/+state/tradables-info.state.service";
import { distinctUntilDictItemChanged } from "@helpers/custom-rxjs";
import { pickManyFromDictByIds } from "@helpers/functions";
import { filter } from "rxjs/operators";

@Injectable({
	providedIn: "root"
})
export class TradablesFacade {
	public tradables$: Observable<Dictionary<Tradable>> = this.tradablesStateService.tradables$;

	constructor(
		private readonly tradablesDataService: TradablesDataService,
		private readonly tradablesInfoStateService: TradablesInfoStateService,
		private readonly tradablesStateService: TradablesStateService,
		private readonly tradablesBridgeService: TradablesBridgeService
	) {
		this.tradablesBridgeService.tradables$.subscribe((tradables) => {
			this.tradablesStateService.setTradables(tradables);
		});
	}

	public getTradableById$(id: string): Observable<Tradable> {
		return combineLatest([
			this.tradablesStateService.selectTradableById(id),
			this.tradablesInfoStateService.selectTradableInfoById(id)
		]).pipe(map(([tradable, tInfo]) => ({ ...tradable, assetInfo: tInfo })));
	}

	public getTradableInfoByTradableId$(id: string): Observable<TradableInfo> {
		return this.tradablesDataService
			.getTradableInfoByTradableId(id)
			.pipe(tap({ next: (info) => this.tradablesInfoStateService.addTradableInfo(id, info) }));
	}

	public selectMany(ids: string[]): Observable<Dictionary<Tradable>> {
		return this.tradables$.pipe(
			// do not emit if tradables are not populated yet
			filter((tradables) => !!tradables && Object.keys(tradables).length > 0),
			map((tradables) => pickManyFromDictByIds(tradables, ids)),
			distinctUntilDictItemChanged()
		);
	}
}
